import { useSoftphone, formatCallDuration, type SoftphoneStatus } from "../../context/SoftphoneContext";
import { formatPhone } from "../../lib/phoneFormat";

/**
 * Global softphone call bar. Mounted once in Layout.tsx.
 *
 * Visibility: hidden when status is "disabled", "ready", or "registering".
 * Slides up from the bottom of the viewport during connecting / ringing /
 * in-call / ending / error states.
 */
export default function SoftphoneCallBar() {
  const sp = useSoftphone();

  if (!sp.isEnabled) return null;
  if (sp.status === "ready" || sp.status === "registering" || sp.status === "disabled") return null;

  // Only show the error bar if there's an active call context. Device-level
  // registration / init errors are now silenced to "disabled" in the context,
  // but as a belt-and-suspenders guard: if we somehow reach "error" with no
  // active call, don't render the bar.
  if (sp.status === "error" && !sp.activeCall) return null;

  // Narrowed status type — what the UI actually has to render labels for.
  type ActiveStatus = Exclude<SoftphoneStatus, "disabled" | "registering" | "ready">;
  const activeStatus = sp.status as ActiveStatus;

  const isInbound = sp.activeCall?.direction === "inbound";
  const isInboundRinging = isInbound && activeStatus === "ringing";
  const isActive = activeStatus === "connecting" || activeStatus === "ringing" || activeStatus === "in-call" || activeStatus === "ending";

  const stateLabel: Record<ActiveStatus, string> = {
    connecting: "Connecting…",
    ringing: isInbound ? "Incoming call" : "Ringing…",
    "in-call": formatCallDuration(sp.durationSec),
    ending: "Ending…",
    error: sp.error ?? "Error",
  };

  const stateColor: Record<ActiveStatus, string> = {
    connecting: "text-amber-300",
    ringing: "text-amber-300",
    "in-call": "text-emerald-300",
    ending: "text-slate-300",
    error: "text-red-300",
  };

  const display = sp.activeCall?.displayName ?? formatPhone(sp.activeCall?.phoneNumber ?? "") ?? "Call";

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-[440px] w-[calc(100%-2rem)] rounded-2xl shadow-2xl backdrop-blur"
      style={{ background: "rgba(42,46,48,0.95)" }}
      role="status"
      aria-live="polite"
    >
      <div className="px-4 py-3 flex items-center gap-3 text-white">
        {/* Status dot */}
        <span
          className={`inline-block w-2.5 h-2.5 rounded-full ${
            activeStatus === "in-call" ? "bg-emerald-400 animate-pulse"
            : activeStatus === "error" ? "bg-red-400"
            : "bg-amber-400 animate-pulse"
          }`}
          aria-hidden
        />

        {/* Caller */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{display}</div>
          <div className={`text-xs font-medium ${stateColor[activeStatus]}`}>{stateLabel[activeStatus]}</div>
        </div>

        {/* Inbound ringing — Accept button (only path the SDK lets us pick up) */}
        {isInboundRinging && (
          <button
            onClick={sp.accept}
            className="p-2.5 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition shadow"
            title="Accept call"
            aria-label="Accept call"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
        )}

        {/* Mute (only during in-call) */}
        {activeStatus === "in-call" && (
          <button
            onClick={sp.toggleMute}
            className={`p-2 rounded-full transition ${
              sp.muted ? "bg-red-500/30 text-red-300 hover:bg-red-500/40" : "bg-white/10 text-white hover:bg-white/20"
            }`}
            title={sp.muted ? "Unmute" : "Mute"}
            aria-label={sp.muted ? "Unmute" : "Mute"}
          >
            {sp.muted ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            )}
          </button>
        )}

        {/* Hangup / Reject — universal red button. Reject for inbound ringing,
            hangup for everything else. SDK API differs but UX is the same. */}
        {isActive && (
          <button
            onClick={isInboundRinging ? sp.reject : sp.hangup}
            className="p-2.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition shadow"
            title={isInboundRinging ? "Reject call" : "End call"}
            aria-label={isInboundRinging ? "Reject call" : "End call"}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
            </svg>
          </button>
        )}

        {/* Retry for error state */}
        {activeStatus === "error" && (
          <button
            onClick={sp.reinit}
            className="px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs font-semibold hover:bg-white/20 transition"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}
